import { useEffect, useRef, useState } from 'react';
import { cn } from 'lib/cn';
import { StoreIcon, ChevronDownIcon, CheckIcon } from 'components/icons';
import { useVendor } from 'features/vendor';

/** Dropdown to switch the active shop (each shop has its own vendor id). */
export function ShopSwitcher() {
  const { shops, vendorId, profile, setActiveVendorId } = useVendor();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  // Nothing to switch between with a single shop.
  if (!shops || shops.length <= 1) return null;

  const activeName = profile?.displayName || profile?.businessName || 'Shop';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex items-center gap-2 rounded-lg border border-surface-300 bg-white px-3 py-1.5 text-sm transition hover:bg-surface-50"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <StoreIcon className="h-4 w-4 text-slate-500" />
        <span className="max-w-[9rem] truncate font-medium text-slate-800">{activeName}</span>
        <ChevronDownIcon className={cn('h-4 w-4 text-slate-400 transition', open && 'rotate-180')} />
      </button>

      {open && (
        <div
          className="absolute left-0 z-40 mt-1 w-64 overflow-hidden rounded-xl border border-surface-200 bg-white py-1 shadow-lg"
          role="listbox"
        >
          <p className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
            Switch shop
          </p>
          {shops.map((shop) => {
            const shopProfile = shop.profile || {};
            const isActive = shopProfile.id === vendorId;
            const location = [shopProfile.city, shopProfile.state].filter(Boolean).join(', ');
            return (
              <button
                key={shopProfile.id}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => {
                  setActiveVendorId(shopProfile.id);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition hover:bg-surface-50',
                  isActive && 'bg-brand-50',
                )}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-slate-800">
                    {shopProfile.displayName || shopProfile.businessName || 'Shop'}
                  </span>
                  {location && <span className="block truncate text-xs text-slate-500">{location}</span>}
                </span>
                {isActive && <CheckIcon className="h-4 w-4 shrink-0 text-brand-600" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ShopSwitcher;
